import React, { useState, useMemo, useEffect, useCallback } from 'react';
import {
    Breadcrumbs,
    Link,
    Card,
    Chip,
    Button,
    Typography,
    CircularProgress,
    Box,
    Checkbox,
    Select,
    Option,
    Input,
    Table,
} from "@mui/joy";
import { ToastContainer, toast } from "react-toastify";
import 'react-toastify/dist/ReactToastify.css';
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import HomeRoundedIcon from "@mui/icons-material/HomeRounded";
import { CheckCircleOutlineRounded, CheckCircleRounded } from "@mui/icons-material";

import ApiService, { GetUser } from '../../axios/Axios';
import LeadsDrawer from "../modals/lead/LeadsDrawer";
import ResponsiveTableWrapper, { FilterBarLeft, ResponsiveFilterBar } from "../ui/ResponsiveTableWrapper";

const priorityColors = { High: 'danger', Medium: 'warning', Low: 'success' };

const sortTasks = (tasks: any[]) => {
    return tasks.slice().sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
};

const formatDateLabel = (value: string) => {
    const date = new Date(value);
    if (isNaN(date.getTime())) {
        return value || "-";
    }
    return date.toLocaleDateString('en-GB', { weekday: 'short', day: '2-digit', month: 'short' });
};

const formatDateWithRelative = (value: string) => {
    const date = new Date(value);
    if (isNaN(date.getTime())) {
        return value || "-";
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const dateOnly = new Date(date);
    dateOnly.setHours(0, 0, 0, 0);

    const diffMs = dateOnly.getTime() - today.getTime();
    const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));

    let relativeLabel = "today";
    if (diffDays < 0) {
        const daysAgo = Math.abs(diffDays);
        relativeLabel = `${daysAgo} day${daysAgo === 1 ? "" : "s"} ago`;
    } else if (diffDays > 0) {
        relativeLabel = `in ${diffDays} day${diffDays === 1 ? "" : "s"}`;
    }

    return `${formatDateLabel(value)} (${relativeLabel})`;
};

const getBooleanStorage = (key: string, fallback = false) => {
    const value = localStorage.getItem(key);
    return value === null ? fallback : value === "true";
};

export default function SalesActions() {
    const location = localStorage.getItem('location')?.toLowerCase().replace(/ /g, '_');
    const [actions, setActions] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const rowsPerPage = 50;
    const [selectedLead, setSelectedLead] = useState(-1);
    const [openLeadModal, setOpenLeadModal] = useState(false);
    const [salesPeople, setSalesPeople] = useState<any[]>([]);
    const [showCompleted, setShowCompleted] = useState(() => getBooleanStorage("salesActions.showCompleted", false));
    const [createdByFilter, setCreatedByFilter] = useState(() => localStorage.getItem("salesActions.createdByFilter") || "all");
    const [selectedDueDate, setSelectedDueDate] = useState("");

    useEffect(() => {
        localStorage.setItem("salesActions.showCompleted", String(showCompleted));
    }, [showCompleted]);

    useEffect(() => {
        localStorage.setItem("salesActions.createdByFilter", createdByFilter || "all");
    }, [createdByFilter]);

    useEffect(() => {
        let isMounted = true;
        const fetchUserAndSalesPeople = async () => {
            try {
                const [userResponse, peopleResponse] = await Promise.all([
                    GetUser(),
                    ApiService.getFranchiseUsers(),
                ]);
                if (!isMounted) return;
                if (userResponse.status === 200) {
                    if (userResponse.data?.id) {
                        setCreatedByFilter((current) => current || String(userResponse.data.id));
                    }
                }
                setSalesPeople(Array.isArray(peopleResponse) ? peopleResponse : []);
            } catch (error) {
                console.error("Failed to fetch filters data:", error);
            }
        };
        fetchUserAndSalesPeople();
        return () => {
            isMounted = false;
        };
    }, []);

    const fetchActions = useCallback(async () => {
        setIsLoading(true);
        try {
            const response = await ApiService.getActions({
                created_by: createdByFilter && createdByFilter !== "all" ? Number(createdByFilter) : undefined,
                show_completed: showCompleted,
                due_date: selectedDueDate || undefined,
                page,
            });
            const fetchedActions = Array.isArray(response.data?.results) ? response.data.results : [];
            setActions(fetchedActions);
            setTotalCount(response.data?.count || 0);
        } catch (error) {
            console.error("Failed to fetch actions:", error);
            toast.error("Could not load actions. Please try again.", { containerId: "callback-reminders" });
            setActions([]);
            setTotalCount(0);
        } finally {
            setIsLoading(false);
        }
    }, [createdByFilter, showCompleted, selectedDueDate, page]);

    useEffect(() => {
        if (!location) {
            setIsLoading(false);
            return;
        }
        fetchActions();
    }, [location, fetchActions]);

    const sortedActions = useMemo(() => sortTasks(actions), [actions]);
    const pageCount = Math.max(1, Math.ceil(totalCount / rowsPerPage));
    const hasPrevPage = page > 1;
    const hasNextPage = page < pageCount;

    useEffect(() => {
        setPage(1);
    }, [createdByFilter, showCompleted, selectedDueDate]);

    const handleToggleComplete = async (id: number, isCurrentlyCompleted: boolean, e: React.MouseEvent) => {
        e.stopPropagation();
        const action = isCurrentlyCompleted ? 'uncomplete' : 'complete';
        try {
            const updatedTask = await ApiService.updateAction(id, { action });
            setActions((prev) => prev.map((a) => (a.id === id ? updatedTask : a)));
            if (updatedTask.isCompleted) {
                toast.success(`"${updatedTask.title}" marked as complete!`, { containerId: "callback-reminders" });
            } else {
                toast.info(`"${updatedTask.title}" marked as incomplete.`, { containerId: "callback-reminders" });
            }
        } catch (error: any) {
            toast.error(`Failed to update status: ${error.message}`, { containerId: "callback-reminders" });
        }
    };

    const handleDrawerClose = () => {
        setOpenLeadModal(false);
        fetchActions();
    };

    return (
        <>
            <ToastContainer containerId={"callback-reminders"} position="bottom-right" autoClose={2500} theme="colored" hideProgressBar />
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Breadcrumbs separator={<ChevronRightRoundedIcon />} size="sm">
                    <Link href="#/" underline="none" color="neutral"><HomeRoundedIcon /></Link>
                    <Link href="#/" fontSize={12} color="neutral">Dashboard</Link>
                    <Typography fontSize={12} fontWeight={600} color="primary">Callback Reminders</Typography>
                </Breadcrumbs>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
                <Box>
                    <Typography level="h3">Callback Reminders</Typography>
                </Box>
            </Box>

            <Card variant="outlined" sx={{ p: 2, mb: 2 }}>
                <ResponsiveFilterBar>
                    <FilterBarLeft>
                        <Box sx={{ minWidth: 220 }}>
                            <Typography level="body-xs" sx={{ mb: 0.5 }}>Created by</Typography>
                            <Select
                                size="sm"
                                value={createdByFilter || null}
                                onChange={(_, value) => {
                                    setCreatedByFilter(value || "all");
                                    setPage(1);
                                }}
                            >
                                <Option value="all">All sales people</Option>
                                {salesPeople.map((person) => (
                                    <Option key={person.id} value={String(person.id)}>
                                        {person.name || person.username}
                                    </Option>
                                ))}
                            </Select>
                        </Box>
                        <Box sx={{ pt: { xs: 0, sm: 3 }, minWidth: 170 }}>
                            <Checkbox
                                label="Show completed"
                                checked={showCompleted}
                                onChange={(event) => {
                                    setShowCompleted(event.target.checked);
                                    setPage(1);
                                }}
                            />
                        </Box>
                        <Box sx={{ minWidth: 180 }}>
                            <Typography level="body-xs" sx={{ mb: 0.5 }}>Due</Typography>
                            <Input
                                size="sm"
                                type="date"
                                value={selectedDueDate}
                                onChange={(event) => {
                                    setSelectedDueDate(event.target.value);
                                    setPage(1);
                                }}
                            />
                        </Box>
                    </FilterBarLeft>
                </ResponsiveFilterBar>
            </Card>

            <Card variant="outlined" sx={{ p: 1.5 }}>
                {isLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                        <CircularProgress />
                    </Box>
                ) : sortedActions.length === 0 ? (
                    <Typography sx={{ textAlign: 'center', p: 2 }} level="body-sm" color="neutral">
                        No callback reminders found for the selected filters.
                    </Typography>
                ) : (
                    <ResponsiveTableWrapper minWidth={1240}>
                        <Table hoverRow stickyHeader size="sm">
                            <thead>
                                <tr>
                                    <th style={{ width: 120 }}>Created</th>
                                    <th style={{ width: 120 }}>Due</th>
                                    <th style={{ width: 220 }}>Customer</th>
                                    <th style={{ width: 320 }}>Description</th>
                                    <th style={{ width: 110 }}>Priority</th>
                                    <th style={{ width: 140 }}>Created by</th>
                                    <th style={{ width: 110 }}>Status</th>
                                    <th style={{ width: 170 }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {sortedActions.map((task) => {
                                    const isCompleted = task.isCompleted;
                                    const isConverted = task.lead_converted;
                                    return (
                                        <tr
                                            key={task.id}
                                            style={{ opacity: isCompleted || isConverted ? 0.7 : 1, cursor: isConverted ? 'default' : 'pointer' }}
                                            onClick={() => {
                                                if (!isConverted && task.lead?.id > 0) {
                                                    setSelectedLead(task.lead.id);
                                                    setOpenLeadModal(true);
                                                }
                                            }}
                                        >
                                            <td>{formatDateWithRelative(task.created_at)}</td>
                                            <td>{formatDateWithRelative(task.date)}</td>
                                            <td>{task.lead?.customer?.full_name || "-"}</td>
                                            <td>{task.description || "-"}</td>
                                            <td>
                                                <Chip size="sm" variant="soft" color={priorityColors[task.priority] || "neutral"}>
                                                    {task.priority || "-"}
                                                </Chip>
                                            </td>
                                            <td>{task.created_by?.username || "-"}</td>
                                            <td>
                                                <Chip size="sm" variant="soft" color={isCompleted ? "success" : "warning"}>
                                                    {isCompleted ? "Completed" : "Open"}
                                                </Chip>
                                            </td>
                                            <td>
                                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                                    <Button
                                                        size="sm"
                                                        variant={isCompleted ? 'outlined' : 'soft'}
                                                        color={isCompleted ? "neutral" : "success"}
                                                        onClick={(e) => handleToggleComplete(task.id, task.isCompleted, e)}
                                                        disabled={isConverted}
                                                        startDecorator={isCompleted ? <CheckCircleOutlineRounded /> : <CheckCircleRounded />}
                                                    >
                                                        {isCompleted ? "Mark Open" : "Complete"}
                                                    </Button>
                                                </Box>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </Table>
                    </ResponsiveTableWrapper>
                )}
            </Card>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2, gap: 2, flexWrap: 'wrap' }}>
                <Typography level="body-sm" color="neutral">
                    {totalCount === 0
                        ? "0 reminders"
                        : `${(page - 1) * rowsPerPage + 1}-${Math.min(page * rowsPerPage, totalCount)} of ${totalCount}`}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button size="sm" variant="outlined" disabled={!hasPrevPage || isLoading} onClick={() => setPage((p) => Math.max(1, p - 1))}>
                        Previous
                    </Button>
                    <Button size="sm" variant="outlined" disabled={!hasNextPage || isLoading} onClick={() => setPage((p) => p + 1)}>
                        Next
                    </Button>
                </Box>
            </Box>

            {openLeadModal && (
                <LeadsDrawer
                    key={`leads-modal-${selectedLead}`}
                    openState={openLeadModal}
                    parentCallback={handleDrawerClose}
                    leadId={selectedLead}
                />
            )}
        </>
    );
}